import type { MailReward, NewsPostFull } from "@/lib/api";
import type { NewsIndexEntry, NewsLanguage } from "@/lib/news-schema";

/** The editor's flat working copy of one post: index-row fields and bodies together. */
export interface Draft {
  id: string;
  titles: Partial<Record<NewsLanguage, string>>;
  bodies: Partial<Record<NewsLanguage, string>>;
  publishAt: string | null;
  expireAt: string | null;
  pinned: boolean;
  draft: boolean;
  order: number;
  rewards: MailReward[];
}

export const REWARD_FIELDS: Record<number, { amount?: boolean; item?: boolean; hero?: boolean; buff?: boolean; recipe?: boolean; iap?: boolean }> = {
  0: { amount: true, item: true }, 1: { amount: true }, 2: { amount: true },
  4: { hero: true }, 10: { amount: true, buff: true }, 11: { amount: true },
  12: { amount: true }, 14: { amount: true }, 15: { amount: true }, 16: { recipe: true }, 100: { amount: true, iap: true },
};

export const blankDraft = (order: number): Draft => ({ id: "", titles: { en: "" }, bodies: { en: "" }, publishAt: null, expireAt: null, pinned: false, draft: true, order, rewards: [] });

export const draftFromPost = (post: NewsPostFull): Draft => ({ id: post.id, titles: post.titles, bodies: post.bodies, publishAt: post.publishAt,
  expireAt: post.expireAt, pinned: post.pinned, draft: post.draft, order: post.order, rewards: post.rewards ?? [] });

export const entryFromDraft = (d: Draft): NewsIndexEntry => ({ id: d.id, titles: d.titles, publishAt: d.publishAt, expireAt: d.expireAt, pinned: d.pinned, draft: d.draft, order: d.order });

export const postFromDraft = (d: Draft): NewsPostFull => ({ id: d.id, titles: d.titles, bodies: d.bodies, publishAt: d.publishAt,
  expireAt: d.expireAt, pinned: d.pinned, draft: d.draft, order: d.order, rewards: d.rewards });

export type Group = "pinned" | "published" | "scheduled" | "draft" | "expired";
export const GROUP_ORDER: Group[] = ["pinned", "published", "scheduled", "draft", "expired"];
export const GROUP_LABEL: Record<Group, string> = { pinned: "Pinned", published: "Published", scheduled: "Scheduled", draft: "Draft", expired: "Expired" };

export function lifecycleGroup(e: NewsIndexEntry, now: number): Group {
  if (e.pinned) return "pinned";
  if (e.draft) return "draft";
  const publishMs = e.publishAt ? Date.parse(e.publishAt) : NaN;
  const expireMs = e.expireAt ? Date.parse(e.expireAt) : NaN;
  if (!Number.isNaN(expireMs) && expireMs < now) return "expired";
  if (!Number.isNaN(publishMs) && publishMs > now) return "scheduled";
  return "published";
}

export const byDisplayOrder = (a: NewsIndexEntry, b: NewsIndexEntry) => a.order - b.order || Date.parse(b.publishAt ?? "0") - Date.parse(a.publishAt ?? "0");
